import React, { useState, useEffect, useCallback, useRef } from "react";
import { Grid, Card, Table, Button, Tag, Alert } from "tabler-react";
import Select from "react-select";
import SiteWrapper from "../../SiteWrapper.react";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import { NotificationManager } from "react-notifications";

const PAGE_SIZE = 100;

// Perfiles que tienen acceso sin restricción por usuario
// 1 = root, 3 = Gerente Patios, 222 = Controlador Patios
const PERFILES_SIN_RESTRICCION = [1, 3, 222, 2331];
// Perfil Administrador (4443): solo estos usuarios específicos pueden ver la vista
const USUARIOS_ADMIN_PERMITIDOS = [146104, 120859, 92644];

const GpsFijosMonitoreoPage = () => {
  // Estados principales
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [remolques, setRemolques] = useState([]);
  const [loadingRemolques, setLoadingRemolques] = useState(false);

  // Control de acceso
  const [accesoPermitido, setAccesoPermitido] = useState(true);

  // GPS Fijos paginados
  const [gpsFijos, setGpsFijos] = useState([]);
  const [gpsFijosPagina, setGpsFijosPagina] = useState(0);
  const [gpsFijosHasMore, setGpsFijosHasMore] = useState(true);
  const [gpsFijosLoading, setGpsFijosLoading] = useState(false);
  const [gpsFijosBusqueda, setGpsFijosBusqueda] = useState("");

  // Vinculaciones
  const [vinculaciones, setVinculaciones] = useState([]);
  // IDs de remolques que pertenecen al cliente seleccionado (para filtrar vinculaciones)
  const [remolquesClienteIds, setRemolquesClienteIds] = useState([]);

  // Selecciones para vincular
  const [remolqueSeleccionado, setRemolqueSeleccionado] = useState(null);
  const [gpsFijoSeleccionado, setGpsFijoSeleccionado] = useState(null);

  // Loading states
  const [loadingVincular, setLoadingVincular] = useState(false);

  // Ref para evitar cargas duplicadas
  const loadingRef = useRef(false);

  // Validar acceso al montar
  useEffect(() => {
    const tienePermiso = validarAcceso();
    setAccesoPermitido(tienePermiso);
    if (tienePermiso) {
      cargarClientes();
    }
  }, []);

  // Determina si el usuario logueado puede ver esta vista
  const validarAcceso = () => {
    try {
      const usuario = JSON.parse(sessionStorage.getItem("usuario") || "{}");
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "[]");

      // Perfiles del usuario (puede tener varios)
      const perfilesUsuario = Array.isArray(currentUser)
        ? currentUser.map((c) => (c.perfil ? c.perfil.id : null)).filter((p) => p !== null)
        : [];

      // Si tiene algún perfil sin restricción, acceso permitido
      const tienePerfilLibre = perfilesUsuario.some((p) => PERFILES_SIN_RESTRICCION.includes(p));
      if (tienePerfilLibre) {
        return true;
      }

      // Si es Administrador (4443), validar que sea uno de los usuarios permitidos
      const esAdministrador = perfilesUsuario.includes(4443);
      if (esAdministrador) {
        return USUARIOS_ADMIN_PERMITIDOS.includes(usuario.id);
      }

      // Cualquier otro caso: sin acceso
      return false;
    } catch (e) {
      console.error("Error validando acceso:", e);
      return false;
    }
  };


  // Cuando cambia el cliente, cargar datos
  useEffect(() => {
    if (clienteSeleccionado) {
      setGpsFijos([]);
      setGpsFijosPagina(0);
      setGpsFijosHasMore(true);
      setGpsFijosBusqueda("");
      cargarGpsFijosPagina(0, true);
      cargarVinculaciones();
      cargarRemolquesPorCliente(clienteSeleccionado.value);
    } else {
      setRemolques([]);
      setGpsFijos([]);
      setVinculaciones([]);
      setRemolquesClienteIds([]);
      setGpsFijosPagina(0);
      setGpsFijosHasMore(true);
    }
  }, [clienteSeleccionado]);

  // Obtener usuarios con patios=true
  const cargarClientes = () => {
    Crud_Catalogos("usuarios/patios", "", "get", "", [], "", "").then((resp) => {
      if (resp) {
        const lista = Array.isArray(resp) ? resp : (resp.content || []);
        setClientes(lista);
      }
    }).catch((err) => console.error("Error cargando clientes:", err));
  };

  /**
   * Carga remolques filtrados por cliente (usuario):
   * 1. Obtener transportistas del usuario: usuarios_transportistas/usuario/{idUsuario}
   * 2. Para cada transportista, obtener sus remolques: remolques_transportistas/id_transportista/{id}
   * 3. Cargar datos de remolques del catálogo
   */
  const cargarRemolquesPorCliente = async (idUsuario) => {
    setLoadingRemolques(true);
    setRemolques([]);
    setRemolquesClienteIds([]);
    try {
      // Paso 1: Obtener transportistas del usuario
      const usuariosTransp = await Crud_Catalogos(
        "usuarios_transportistas/usuario/" + idUsuario, "", "get", "", [], "", ""
      );

      if (!usuariosTransp || !Array.isArray(usuariosTransp) || usuariosTransp.length === 0) {
        setLoadingRemolques(false);
        return;
      }

      // Extraer IDs únicos de transportistas
      const idsTransportistas = [...new Set(
        usuariosTransp.map((ut) => ut.idTransportista).filter((id) => id !== null)
      )];

      if (idsTransportistas.length === 0) {
        setLoadingRemolques(false);
        return;
      }

      // Paso 2: Para cada transportista, obtener sus remolques
      const promesasRemolques = idsTransportistas.map((idTransp) =>
        Crud_Catalogos("remolques_transportistas/id_transportista/" + idTransp, "", "get", "", [], "", "")
          .catch(() => [])
      );

      const resultadosRemolques = await Promise.all(promesasRemolques);

      // Recopilar IDs únicos de remolques activos
      const idsRemolquesSet = new Set();
      resultadosRemolques.forEach((resp) => {
        const lista = Array.isArray(resp) ? resp : (resp && resp.content ? resp.content : []);
        lista.forEach((rt) => {
          if (rt.estatus !== false && rt.idRemolque) {
            idsRemolquesSet.add(rt.idRemolque);
          }
        });
      });

      if (idsRemolquesSet.size === 0) {
        setLoadingRemolques(false);
        return;
      }

      // Guardar los IDs de remolques del cliente para filtrar las vinculaciones
      setRemolquesClienteIds([...idsRemolquesSet]);

      // Paso 3: Cargar catálogo de remolques y filtrar
      const todosRemolques = await Crud_Catalogos("remolques", "", "get", "", [], "", "");
      if (todosRemolques) {
        const listaRemolques = Array.isArray(todosRemolques)
          ? todosRemolques
          : (todosRemolques.content || []);
        const remolquesFiltrados = listaRemolques.filter((r) => idsRemolquesSet.has(r.idRemolque));
        setRemolques(remolquesFiltrados);
      }
    } catch (err) {
      console.error("Error cargando remolques por cliente:", err);
    } finally {
      setLoadingRemolques(false);
    }
  };

  // Carga paginada de GPS fijos (tabla gps WHERE fijo=true)
  const cargarGpsFijosPagina = useCallback((pagina, reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setGpsFijosLoading(true);

    Crud_Catalogos("gps/fijos/pagina/" + pagina + "/total/" + PAGE_SIZE, "", "get", "", [], "", "")
      .then((resp) => {
        if (resp) {
          let datos = [];
          let totalPages = 0;

          if (resp.content && Array.isArray(resp.content)) {
            datos = resp.content;
            totalPages = resp.totalPages || 0;
          } else if (Array.isArray(resp)) {
            datos = resp;
          }

          if (reset) {
            setGpsFijos(datos);
          } else {
            setGpsFijos((prev) => [...prev, ...datos]);
          }

          const siguientePagina = pagina + 1;
          setGpsFijosPagina(siguientePagina);
          setGpsFijosHasMore(siguientePagina < totalPages);
        }
      })
      .catch((err) => console.error("Error cargando GPS fijos:", err))
      .finally(() => {
        setGpsFijosLoading(false);
        loadingRef.current = false;
      });
  }, []);

  // Buscar GPS fijos por nombre/IMEI
  const buscarGpsFijos = useCallback((texto) => {
    if (texto.length < 3) return;
    setGpsFijosLoading(true);

    Crud_Catalogos(
      "gps/fijos/nombre?NOMBRE=" + encodeURIComponent(texto),
      "", "get", "", [], "", ""
    )
      .then((resp) => {
        if (resp) {
          if (Array.isArray(resp)) {
            setGpsFijos(resp);
          } else if (resp.content && Array.isArray(resp.content)) {
            setGpsFijos(resp.content);
          }
          setGpsFijosHasMore(false);
        }
      })
      .catch((err) => console.error("Error buscando GPS fijos:", err))
      .finally(() => setGpsFijosLoading(false));
  }, []);

  const handleGpsFijosScrollToBottom = () => {
    if (gpsFijosHasMore && !gpsFijosLoading && !gpsFijosBusqueda) {
      cargarGpsFijosPagina(gpsFijosPagina);
    }
  };

  const handleGpsFijosInputChange = (inputValue) => {
    setGpsFijosBusqueda(inputValue);
    if (inputValue.length >= 3) {
      buscarGpsFijos(inputValue);
    } else if (inputValue.length === 0) {
      setGpsFijos([]);
      setGpsFijosPagina(0);
      setGpsFijosHasMore(true);
      cargarGpsFijosPagina(0, true);
    }
  };

  const cargarVinculaciones = () => {
    Crud_Catalogos("remolquesGpsFijos", "", "get", "", [], "", "").then((resp) => {
      if (resp) {
        if (Array.isArray(resp)) {
          setVinculaciones(resp);
        } else if (resp.content && Array.isArray(resp.content)) {
          setVinculaciones(resp.content);
        }
      }
    }).catch((err) => console.error("Error cargando vinculaciones:", err));
  };

  // IDs ya vinculados activamente
  const remolquesVinculadosIds = vinculaciones
    .filter((v) => v.estatus === true)
    .map((v) => v.remolque.idRemolque);

  const gpsVinculadosIds = vinculaciones
    .filter((v) => v.estatus === true)
    .map((v) => v.gps.id);

  // Vinculaciones activas (todo el sistema)
  const vinculacionesActivas = vinculaciones.filter((v) => v.estatus);

  // Vinculaciones activas del cliente seleccionado (filtradas por sus remolques)
  const vinculacionesCliente = vinculacionesActivas.filter(
    (v) => v.remolque && remolquesClienteIds.includes(v.remolque.idRemolque)
  );

  // Opciones para dropdowns
  const remolquesOpciones = remolques
    .filter((r) => !remolquesVinculadosIds.includes(r.idRemolque))
    .map((r) => ({
      value: r.idRemolque,
      label: `${r.economico || ""} - ${r.placa || ""} (${r.remolquesTipos ? r.remolquesTipos.nombre : "Sin tipo"})`,
    }));

  const gpsFijosOpciones = gpsFijos
    .filter((g) => !gpsVinculadosIds.includes(g.id))
    .map((g) => ({
      value: g.id,
      label: `${g.nombre || ""} - ${g.imei || ""}`,
    }));

  const clientesOpciones = clientes.map((u) => ({
    value: u.id,
    label: `${u.persona ? (u.persona.nombre + " " + u.persona.aPaterno) : u.username}`,
  }));

  // Vincular
  const handleVincular = () => {
    if (!remolqueSeleccionado || !gpsFijoSeleccionado) {
      NotificationManager.warning("Selecciona un remolque y un GPS fijo para vincular");
      return;
    }

    setLoadingVincular(true);
    const body = {
      remolque: { idRemolque: remolqueSeleccionado.value },
      gps: { id: gpsFijoSeleccionado.value },
    };

    Crud_Catalogos("remolquesGpsFijos", "", "post", "", body, "", "").then((resp) => {
      if (resp && resp.idRemolqueGpsFijo) {
        NotificationManager.success("Vinculación creada exitosamente");
        setRemolqueSeleccionado(null);
        setGpsFijoSeleccionado(null);
        cargarVinculaciones();
      } else if (typeof resp === "string") {
        NotificationManager.error(resp);
      }
      setLoadingVincular(false);
    }).catch((err) => {
      NotificationManager.error("Error al vincular: " + (err.message || "Error desconocido"));
      setLoadingVincular(false);
    });
  };

  // Desvincular
  const handleDesvincular = (idRemolqueGpsFijo) => {
    Crud_Catalogos("remolquesGpsFijos/desvincular/" + idRemolqueGpsFijo, "", "put", "", [], "", "").then((resp) => {
      if (resp && resp.idRemolqueGpsFijo) {
        NotificationManager.success("Desvinculación exitosa");
        cargarVinculaciones();
      } else if (typeof resp === "string") {
        NotificationManager.error(resp);
      }
    }).catch((err) => {
      NotificationManager.error("Error al desvincular: " + (err.message || "Error desconocido"));
    });
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "-";
    const d = new Date(fecha);
    return d.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <SiteWrapper>
      <div className="container-xl">
        <div className="page-header">
          <div className="page-title">GPS Fijos - Vinculación con Remolques</div>
        </div>

        {!accesoPermitido ? (
          <Grid.Row>
            <Grid.Col sm={12} lg={12}>
              <Alert type="danger">
                No tienes permisos para acceder a esta vista. Contacta al administrador del sistema.
              </Alert>
            </Grid.Col>
          </Grid.Row>
        ) : (
        <>
        <Grid.Row>
          <Grid.Col sm={12} lg={12}>
            <Card>
              <Card.Header>
                <Card.Title>Seleccionar Cliente</Card.Title>
              </Card.Header>
              <Card.Body>
                <Select
                  value={clienteSeleccionado}
                  onChange={(opcion) => {
                    setClienteSeleccionado(opcion);
                    setRemolqueSeleccionado(null);
                    setGpsFijoSeleccionado(null);
                  }}
                  options={clientesOpciones}
                  placeholder="Selecciona un cliente..."
                  isClearable
                />
              </Card.Body>
            </Card>
          </Grid.Col>
        </Grid.Row>

        {clienteSeleccionado && (
          <>
            <Grid.Row>
              <Grid.Col sm={12} lg={12}>
                <Card>
                  <Card.Header>
                    <Card.Title>Vincular GPS Fijo con Remolque</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <Grid.Row>
                      <Grid.Col sm={12} lg={5}>
                        <label className="form-label">Remolque</label>
                        <Select
                          value={remolqueSeleccionado}
                          onChange={setRemolqueSeleccionado}
                          options={remolquesOpciones}
                          placeholder="Selecciona un remolque..."
                          isClearable
                          isLoading={loadingRemolques}
                          noOptionsMessage={() =>
                            loadingRemolques
                              ? "Cargando remolques..."
                              : "No hay remolques disponibles para este cliente"
                          }
                        />
                      </Grid.Col>
                      <Grid.Col sm={12} lg={5}>
                        <label className="form-label">GPS Fijo (escribe para buscar o desplaza para más)</label>
                        <Select
                          value={gpsFijoSeleccionado}
                          onChange={setGpsFijoSeleccionado}
                          options={gpsFijosOpciones}
                          placeholder="Busca por nombre o IMEI..."
                          isClearable
                          isLoading={gpsFijosLoading}
                          onMenuScrollToBottom={handleGpsFijosScrollToBottom}
                          onInputChange={handleGpsFijosInputChange}
                          filterOption={() => true}
                          noOptionsMessage={() =>
                            gpsFijosLoading
                              ? "Cargando..."
                              : gpsFijosBusqueda.length > 0 && gpsFijosBusqueda.length < 3
                              ? "Escribe al menos 3 caracteres para buscar"
                              : "No hay GPS fijos disponibles"
                          }
                        />
                      </Grid.Col>
                      <Grid.Col sm={12} lg={2} className="d-flex align-items-end">
                        <Button
                          color="primary"
                          block
                          onClick={handleVincular}
                          disabled={loadingVincular || !remolqueSeleccionado || !gpsFijoSeleccionado}
                        >
                          {loadingVincular ? "Vinculando..." : "Vincular"}
                        </Button>
                      </Grid.Col>
                    </Grid.Row>
                  </Card.Body>
                </Card>
              </Grid.Col>
            </Grid.Row>

            <Grid.Row>
              <Grid.Col sm={12} lg={12}>
                <Card>
                  <Card.Header>
                    <Card.Title>
                      Vinculaciones — {vinculacionesCliente.length} activas de este cliente · {vinculacionesActivas.length} en total
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    {vinculacionesCliente.length === 0 ? (
                      <Alert type="info">
                        No hay vinculaciones activas para este cliente.
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table table-striped table-hover">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColHeader>ID</Table.ColHeader>
                              <Table.ColHeader>Remolque</Table.ColHeader>
                              <Table.ColHeader>Placa</Table.ColHeader>
                              <Table.ColHeader>GPS Fijo</Table.ColHeader>
                              <Table.ColHeader>IMEI</Table.ColHeader>
                              <Table.ColHeader>Estatus</Table.ColHeader>
                              <Table.ColHeader>Fecha Vinculación</Table.ColHeader>
                              <Table.ColHeader>Acciones</Table.ColHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {vinculacionesCliente.map((v) => (
                              <Table.Row key={v.idRemolqueGpsFijo}>
                                <Table.Col>{v.idRemolqueGpsFijo}</Table.Col>
                                <Table.Col>{v.remolque ? v.remolque.economico : "-"}</Table.Col>
                                <Table.Col>{v.remolque ? v.remolque.placa : "-"}</Table.Col>
                                <Table.Col>{v.gps ? v.gps.nombre : "-"}</Table.Col>
                                <Table.Col>{v.gps ? v.gps.imei : "-"}</Table.Col>
                                <Table.Col>
                                  <Tag color={v.estatus ? "green" : "red"}>
                                    {v.estatus ? "Vinculado" : "Desvinculado"}
                                  </Tag>
                                </Table.Col>
                                <Table.Col>{formatFecha(v.fechaVinculacion)}</Table.Col>
                                <Table.Col>
                                  {v.estatus && (
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleDesvincular(v.idRemolqueGpsFijo)}
                                    >
                                      Desvincular
                                    </Button>
                                  )}
                                </Table.Col>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Grid.Col>
            </Grid.Row>
          </>
        )}
        </>
        )}
      </div>
    </SiteWrapper>
  );
};

export default GpsFijosMonitoreoPage;
